import {Input} from 'antd';
import Button from 'antd/es/button/button';
import clone from 'lodash/clone';
import * as React from 'react';
import SimpleReactValidator from 'simple-react-validator';
import {BasicModal} from '../../components/basic-modal';
import {createEntityAndGeometry} from '../../http/createEntityAndGeometry';
import {requestContactByPocId} from '../../http/requestContactByPocId';
import {IContactNode, IFeature_CONO} from '../../types';
import {maxLengthInput, maxLengthTextArea} from '../../utils/constants';
import {getFeatureType} from '../../utils/getFeatureType';
import {getFormattedDate, getFormattedDateUserReadable} from '../../utils/getFormattedDate';
import {getLayerName} from '../../utils/getLayerName';
import {IPopupInfo} from '../../utils/getPopupInfo';
import {isUrl} from '../../utils/isUrl';
import {html_space} from '../../utils/space';
import {validateName, validatePhoneNumber, validateText} from '../../utils/validation';
import {ContactDetails} from './contact-details';
import {DateLastChanged} from './date-last-changed';
import {LinkedContactPopup} from './linked-contact-popup';
import {newEntityProperties} from './map';
import {NewContactPopup} from './newContactPopup';
import {renderUrl} from "../../utils/renderUrl";

const { TextArea } = Input;

interface IQueuedAction {
    infoType: contactInfoType;
    popupInfo: IPopupInfo;
    contactInfo: IContactNode;
}

interface IProps {
    popupInfo: IPopupInfo;
    save?: any;
    cancel?: any;
    delete: any;
    buffer: any;
    isCreatingNewEntity: boolean;
    isEditing: boolean;
    layers: string[];
    layerState: {[key: string]: any};
    databaseLayerIds: {[key: string]: any};
    showContactDetails: boolean;
    // create callbacks
    createContact: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    editContact: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    createEntityContactLink: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    removeEntityContactLink: (feature: IFeature_CONO, contactInfo: IContactNode) => void;
    editGeometry: any;
    toggleEditing: (isEditing?: boolean) => void;
    resetFeature: () => void;
    // callbacks
    toggleContactDetails: (c: boolean) => void;
}

interface IState {
    geometryEdited: boolean;
    contactsEdited: boolean;
    popupInfo?: IPopupInfo;
    contactInfo?: IContactNode;
    newPocId?: string|number;
    contactInfoType?: number;
    contactValid: boolean;
    entityValid: boolean;
}

enum contactInfoType {
    DETAILS = 0,
    CONTACT_LINK = 1,
    NEW_CONTACT = 2,
    DETAILS_EDIT = 3,
    REMOVE_LINK = 4,
    NEW_ENTITY = 5,
    SAVE_ENTITY = 6,
}

enum entityKeys {
    name = 'name',
}

enum contactKeys {
    name = 'name',
    email = 'email',
    // land = 'land',
    cell = 'cell',
    pubconinfo = 'pubconinfo',
    instruc = 'instruc',
}

/***
 *
 *

 Label : Bogus Value
 Name : .Unknown
 Cell : .Unknown
 Land : .Unknown
 Email : .Unknown
 PubConInfo : .none
 Instruc : .none
 DateLastChanged : 01Jan1970
 *
 */

const defaultContactInfo = () => ({
    pocid: undefined,
    name: '.Unknown',
    email: '.Unknown',
    cell: '.Unknown',
    land: '.Unknown',
    pubconinfo: '.none',
    instruc: '.none',
    datelastchanged: new Date().toISOString(),
});

const className = 'mapboxgl-popup-content-text';
const rowClass = 'mapboxgl-popup-content-row';
const selectClass = 'popup-user-select';

export class PopupContents extends React.PureComponent <IProps, IState> {
    public priorityKeys = ['name', 'address', 'city', 'state', 'zip', 'notes', 'phone', 'email', 'websiteurl'];
    public priorityLabels = ['Name', 'Address', 'City', 'State', 'Zip', 'Jurisdiction',
        'Phone', 'Email', 'Website'];
    public state: IState = {
        geometryEdited: false,
        contactsEdited: false,
        popupInfo: undefined,
        contactInfo: undefined,
        newPocId: undefined,
        contactInfoType: undefined,
        contactValid: true,
        entityValid: true,
    };
    public queuedSaveActions: IQueuedAction[] = [];
    public validator: SimpleReactValidator | undefined;

    public toggleEditing = () => {
        this.props.toggleEditing();
    }

    public cancelEditing = () => {
        this.setState({
            popupInfo: this.props.popupInfo,
        });
        if (this.props.isCreatingNewEntity) {
            this.props.cancel();
        } else {
            this.props.toggleEditing(false);
        }
        if (this.props.editGeometry) {
            this.props.resetFeature();
            this.setState({
                geometryEdited: false,
            });
        }
    }

    public buffer = () => {
        this.props.buffer(this.props.popupInfo.fullUid);
    }

    public delete = () => {
        this.props.delete(this.props.popupInfo.fullUid, this.props.popupInfo.type);
    }

    public saveEditing = () => {
        if (!this.state.entityValid) {
            return;
        }
        const popupInfo = this.state.popupInfo || this.props.popupInfo;
        const newEntityPropertyKeys = Object.keys(newEntityProperties);
        popupInfo.description.forEach((value, i) => {
            const originalKey = this.props.popupInfo.descriptionKeys[i];
            if (value !== '' && typeof value !== 'undefined') {
                popupInfo[originalKey] = value;
            } else if (newEntityPropertyKeys.indexOf(originalKey) !== -1) {
                popupInfo[originalKey] = newEntityProperties[originalKey];
                popupInfo.description[i] = newEntityProperties[originalKey];
            } else {
                // do nothing
            }
        });
        newEntityPropertyKeys.forEach(key => {
            if (!popupInfo[key] || popupInfo[key] === '') {
                popupInfo[key] = newEntityProperties[key];
            }
        });
        if (this._shouldSaveEntity()) {
            this._queueContactSaveActions(contactInfoType.SAVE_ENTITY, popupInfo, {} as any);
        }
        this._resolveQueuedActions();
        this.toggleEditing();
        // this.props.save(popupInfo);

    }

    public _shouldSaveEntity() {
        if (this.state.geometryEdited || this.state.contactsEdited) { return true; }
        if (!this.state.popupInfo) { return false; }
        let shouldSave = false;
        const keys = Object.keys(this.props.popupInfo)
            .filter(key => this.props.popupInfo[key] !== this.state?.popupInfo![key]);
        keys.forEach(key => {
            if (key !== 'contacts' && key !== 'datelastchanged') {
                shouldSave = true;
            }
        });
        return shouldSave;
    }

    public updateDescription(event: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>, i: number) {
        const value = event.target.value;
        const description = this.props.popupInfo.description.slice();
        if (this.state.popupInfo && this.state.popupInfo.description) {
            this.state.popupInfo.description.forEach((d, idx) => {
                description[idx] = d;
            });
        }
        description[i] = value;
        const popupInfo = {
            ...this.props.popupInfo,
            ...this.state.popupInfo,
            description,
        };
        const entityValid = this._validateEntityKeys(popupInfo);
        this.setState({
            popupInfo,
            entityValid,
        });
        event.target.focus();
    }
    public updateKey(event: React.SyntheticEvent, key: string) {
        const value = (event.target as HTMLInputElement).value;
        const popupInfo: IPopupInfo = {
            ...this.props.popupInfo,
            ...this.state.popupInfo,
        };

        if (key === 'type') {
            // todo: better way to do this
            popupInfo.oldType = popupInfo.type;
        }

        popupInfo[key] = value;

        const entityValid = this._validateEntityKeys(popupInfo);
        this.setState({
            popupInfo,
            entityValid,
        });
    }

    public editGeometry = () => {
        this.setState({
            geometryEdited: true,
        });
        this.props.editGeometry(this.props.popupInfo.fullUid);
    }

    public _showContactDetails(contact: IContactNode) {
        this.props.toggleContactDetails(true);
        this.setState({
            contactInfoType: contactInfoType.DETAILS,
            contactInfo: contact,
        });
    }

    public _editContactDetails(contact: IContactNode) {
        this.props.toggleContactDetails(true);
        this.setState({
            contactInfoType: contactInfoType.DETAILS_EDIT,
            contactInfo: contact,
        });
    }

    // tslint:disable-next-line:variable-name
    public _createLinkToContact = () => {
        this.props.toggleContactDetails(true);
        this.setState({
            contactInfoType: contactInfoType.CONTACT_LINK,
            contactInfo: undefined,
        });
    }

    // tslint:disable-next-line:variable-name
    public _removeLinkToContact = (contact: IContactNode) => {
        this.props.toggleContactDetails(true);
        this.setState({
            contactInfoType: contactInfoType.REMOVE_LINK,
            contactInfo: contact,
        });
    }

    // tslint:disable-next-line:variable-name
    public _createNewContact = () => {
        this.props.toggleContactDetails(true);
        this.setState({
            contactInfoType: contactInfoType.NEW_CONTACT,
            contactInfo: defaultContactInfo(),
        });
    }

    // tslint:disable-next-line:variable-name
    public _resolveContactInfo = (contactInfo: IContactNode) => {
        const keys = Object.keys(contactInfo) as Array<keyof IContactNode>;
        const defaultInfo: IContactNode = defaultContactInfo();
        keys.forEach((key) => {
            if (!contactInfo[key] || contactInfo[key] === '') {
                // @ts-ignore
                contactInfo[key] = defaultInfo[key] ? defaultInfo[key] : contactInfo[key];
            }
        });
        return contactInfo;
    }

    // tslint:disable-next-line:variable-name
    public _updateNewPocId = (event: any) => {
        const pocid = event.target.value;
        this.setState({
            newPocId: pocid,
        });
        if (pocid && !isNaN(Number(pocid))) {
            requestContactByPocId(pocid).then((result) => {
                this.setState({contactInfo: result as IContactNode});
            });
        }
    }

    public async _resolveQueuedActions() {
        let pendingPopupInfo = {};
        if (this.queuedSaveActions && this.queuedSaveActions.length > 0) {
            while (this.queuedSaveActions.length > 0) {
                const action = this.queuedSaveActions.pop();
                const {
                    infoType,
                    popupInfo,
                } = action!;
                let {
                    contactInfo,
                } = action!;
                const {feature, type} = popupInfo;
                switch (infoType) {
                    case contactInfoType.NEW_CONTACT:
                        delete contactInfo.pocid;
                        contactInfo = this._resolveContactInfo(contactInfo);
                        await this.props.createContact(feature, contactInfo);
                        break;
                    case contactInfoType.CONTACT_LINK:
                        await this.props.createEntityContactLink(feature, contactInfo);
                        break;
                    case contactInfoType.DETAILS_EDIT:
                        contactInfo = this._resolveContactInfo(contactInfo);
                        await this.props.editContact(feature, contactInfo);
                        break;
                    case contactInfoType.REMOVE_LINK:
                        await this.props.removeEntityContactLink(feature, contactInfo);
                        break;
                    case contactInfoType.NEW_ENTITY:
                        // create an entity first
                        if (!type) {
                            // tslint:disable-next-line:no-console
                            console.warn('missing layer type');
                            break;
                        }
                        const layerid = this.props.databaseLayerIds[type!];
                        const {
                            entity,
                        } = await createEntityAndGeometry(feature, type!, layerid);
                        if (entity) {
                            popupInfo.fullUid = entity.fullUid!;
                            popupInfo.feature.properties.fullUid = entity.fullUid!;
                        }
                        pendingPopupInfo = popupInfo;
                        break;
                    case contactInfoType.SAVE_ENTITY:
                        pendingPopupInfo = {...popupInfo, ...pendingPopupInfo};
                        this.props.save(pendingPopupInfo);
                        break;
                    default:
                    // nothing
                }
            }
        }
    }

    public _queueContactSaveActions(infoType: contactInfoType, popupInfo: IPopupInfo, contactInfo: IContactNode) {
        let existing;
        switch (infoType) {
            case contactInfoType.REMOVE_LINK:
                existing = this.queuedSaveActions.filter(action => {
                    return (action.infoType === contactInfoType.CONTACT_LINK ||
                        action.infoType === contactInfoType.NEW_CONTACT) &&
                        action.contactInfo.pocid === contactInfo.pocid;
                });
                if (existing.length > 0) {
                    this.queuedSaveActions = this.queuedSaveActions.filter(action => {
                        return (action.infoType === contactInfoType.CONTACT_LINK ||
                            action.infoType === contactInfoType.NEW_CONTACT) &&
                            action.contactInfo.pocid === contactInfo.pocid;
                    });
                    return; // don't queue this action
                }
                break;
            case contactInfoType.DETAILS_EDIT:
                existing = this.queuedSaveActions.filter(action => {
                    return action.infoType === contactInfoType.DETAILS_EDIT &&
                        action.contactInfo.pocid === contactInfo.pocid;
                });
                if (existing.length > 0) {
                    this.queuedSaveActions = this.queuedSaveActions.filter(action => {
                        return action.infoType !== contactInfoType.DETAILS_EDIT &&
                            action.contactInfo.pocid !== contactInfo.pocid;
                    });
                }
                // this should merge edits
                break;
            case contactInfoType.NEW_ENTITY:
            // save entity before creating a contact link
            case contactInfoType.SAVE_ENTITY:
            default:
            // nothing
        }
        this.queuedSaveActions.unshift(
            {
                infoType,
                popupInfo: clone(popupInfo),
                contactInfo: clone(contactInfo),
            },
        );
    }

    // tslint:disable-next-line:variable-name
    public _resolveModal = () => {
        const infoType = this.state.contactInfoType;
        const contactInfo: IContactNode & {contactInfoType?: contactInfoType, pocid?: any} = this.state.contactInfo!;
        const popupInfo = this.state.popupInfo || this.props.popupInfo;
        const newEntity = this.props.isCreatingNewEntity;
        let contacts: IContactNode[] = popupInfo.contacts ? popupInfo.contacts.slice() : [];
        contactInfo.contactInfoType = infoType;
        switch (infoType) {
            case contactInfoType.NEW_CONTACT:
                this.setState({contactsEdited: true});
                contactInfo.pocid = new Date().toISOString(); // temporary pocid
                if (newEntity) {
                    // If we are creating a new entity, queue an additional save action
                    this._queueContactSaveActions(contactInfoType.NEW_ENTITY, popupInfo, contactInfo);
                }
                this._queueContactSaveActions(contactInfoType.NEW_CONTACT, popupInfo, contactInfo);
                contacts.push(contactInfo);
                break;
            case contactInfoType.CONTACT_LINK:
                this.setState({contactsEdited: true});
                if (newEntity) {
                    // If we are creating a new entity, queue an additional save action
                    this._queueContactSaveActions(contactInfoType.NEW_ENTITY, popupInfo, contactInfo);
                }
                this._queueContactSaveActions(contactInfoType.CONTACT_LINK, popupInfo, contactInfo);
                contacts.push(contactInfo);
                break;
            case contactInfoType.DETAILS_EDIT:
                // this.props.editContact(popupInfo, contactInfo);
                this._queueContactSaveActions(contactInfoType.DETAILS_EDIT, popupInfo, contactInfo);
                contacts = contacts.filter((contact) => contact.pocid !== contactInfo.pocid);
                contacts.push(contactInfo);
                break;
            case contactInfoType.REMOVE_LINK:
                this.setState({contactsEdited: true});
                this._queueContactSaveActions(contactInfoType.REMOVE_LINK, popupInfo, contactInfo);
                contacts = contacts.filter((contact) => contact.pocid !== contactInfo.pocid);
                // this.props.removeEntityContactLink(popupInfo, contactInfo);
                break;
            case contactInfoType.DETAILS:
            default:
            // nothing
        }
        this.setState({popupInfo: {
                ...popupInfo, contacts,
            }});
        this._hideContactDetails();
    }

    // tslint:disable-next-line:variable-name
    public _hideContactDetails = () => {
        this.props.toggleContactDetails(false);
        this.setState({
            contactInfo: undefined,
        });
    }

    public _renderDateLastChanged(date: string) {
        return (<>
            <strong>Date Last Changed: </strong>{getFormattedDateUserReadable(date)} ({getFormattedDate(date)})
        </>);
    }

    // tslint:disable-next-line:variable-name
    public _renderContactPopup = () => {
        const infoType = this.state.contactInfoType;
        const contactValid = this.state.contactValid;
        const contactInfo = this.state.contactInfo;
        switch (infoType) {
            case contactInfoType.DETAILS_EDIT:
            case contactInfoType.NEW_CONTACT:
                return <NewContactPopup
                    infoType={infoType} contactValid={contactValid} contactInfo={contactInfo}
                    updateContactKey={this._updateContactKey}
                    validateContactKey={this._validateContactKey}
                    getValidationMessage={this._getValidationMessage}
                />;
            case contactInfoType.CONTACT_LINK:

                return <LinkedContactPopup
                    contactInfo={contactInfo}
                    newPocId={this.state.newPocId!}
                    updateNewPocId={this._updateNewPocId}
                    className={className}
                    rowClass={rowClass}
                    validationMessage={
                        this.validator ?
                            this.validator.message('POCID', this.state.newPocId, 'required|numeric') :
                            null
                    }
                />;
            case contactInfoType.DETAILS:
            default:
                return <ContactDetails contactInfo={contactInfo} className={className} />;
        }
    }

    // tslint:disable-next-line:variable-name
    public _getValidationMessage = (key: keyof IContactNode) => {
        const contactInfo = this.state.contactInfo!;
        const value = contactInfo[key]!;
        if (!contactInfo) {
            return '';
        }
        switch (key) {
            case contactKeys.name:
                if (value && (value as string).length > 250) {
                    return 'reached character limit';
                }
                return '';
            case contactKeys.email:
                return '';
            case contactKeys.cell:
            case contactKeys.instruc:
            case contactKeys.pubconinfo:
                if (value && (value as string).length > 1000) {
                    return 'reached character limit';
                }
                return '';
            default:
                return '';
        }
    }

    // tslint:disable-next-line:variable-name
    public _validateContactKey = (key: keyof IContactNode, contactInfo = this.state.contactInfo) => {
        if (!contactInfo) {
            return true;
        }
        const value: string = contactInfo[key] as string;
        if (value === null) {
            return true;
        }
        switch (key) {
            case contactKeys.name:
                return validateName(value, 250);
            case contactKeys.email:
                return validateName(value, 250);
            // return value ? validateEmail(value): true;
            case contactKeys.cell:
                return value ? validatePhoneNumber(value) : true;
            case contactKeys.instruc:
            case contactKeys.pubconinfo:
                return value ? validateText(value, 1000) : true;
            default:
                return true;
        }
    }

    // tslint:disable-next-line:variable-name
    public _validateEntityKey = (key: keyof IPopupInfo, popupInfo = this.state.popupInfo) => {
        if (!popupInfo) {
            return true;
        }
        const value = popupInfo![key];
        switch (key) {
            case entityKeys.name:
                return validateName(value);
            default:
                return (typeof value === 'string') ? validateText(value) : true;
        }
    }

    // tslint:disable-next-line:variable-name
    public _validateEntityDescriptionKey = (index: number, popupInfo = this.state.popupInfo) => {
        if (!popupInfo) {
            return true;
        }
        const value = popupInfo.description[index];
        return validateText(value);
    }

    public _validateEntityKeys(popupInfo: IPopupInfo) {
        const keys: Array<keyof IPopupInfo> = Object.keys(popupInfo);
        let valid = true;
        if (!popupInfo.type) {
            valid = false;
        }
        while (keys.length > 0 && valid) {
            if (!valid) { break; }
            const val: keyof IPopupInfo = keys.pop()!;
            valid = this._validateEntityKey(val, popupInfo);
        }
        let i = -1;
        while (++i < popupInfo.description.length) {
            if (!valid) { break; }
            valid = this._validateEntityDescriptionKey(i, popupInfo);
        }
        return valid;
    }

    public _validateContactKeys(contactInfo: IContactNode) {
        const keys = Object.keys(contactInfo) as Array<keyof IContactNode>;
        let valid = true;
        while (keys.length > 0 && valid) {
            valid = !!this._validateContactKey(keys.pop()!, contactInfo);
        }
        return valid;
    }

    // tslint:disable-next-line:variable-name
    public _updateContactKey = (key: string, e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>) => {
        const value = e.target.value!;
        // @ts-ignore
        const contactInfo = {
            ...this.state.contactInfo,
            [key]: value,
            // datelastchanged: new Date().toISOString(),
        } as IContactNode;
        const contactValid = this._validateContactKeys(contactInfo);
        this.setState({
            contactInfo,
            contactValid,
        });
        e.target.focus();
    }

    // tslint:disable-next-line:variable-name
    public _getContactPopupTitle = () => {
        const infoType = this.state.contactInfoType!;
        switch (infoType) {
            case contactInfoType.NEW_CONTACT:
                return 'Create New Contact';
            case contactInfoType.CONTACT_LINK:
                return 'Create Link To Contact';
            case contactInfoType.DETAILS_EDIT:
                return 'Edit Contact Information';
            case contactInfoType.REMOVE_LINK:
                return 'Remove Contact';
            case contactInfoType.DETAILS:
            default:
                return 'Contact Information';
        }
    }

    public renderContact(contact: IContactNode, index: number) {
        const {isEditing} = this.props;
        return <div className={'description_contactInfo_row'} key={`contact_${contact.pocid}_${index}`}>
            <div className={'popup-user-select'}>{contact.name} </div>
            <div>
                <Button size={'small'} style={{marginLeft: 'auto'}}
                        onClick={() => this._showContactDetails(contact)}>Details</Button>
                {isEditing && <><Button size={'small'} style={{marginLeft: 'auto'}}
                                        onClick={() => this._editContactDetails(contact)}>Edit</Button>
                    <Button size={'small'} style={{marginLeft: 'auto'}}
                            onClick={() => this._removeLinkToContact(contact)}>Remove</Button>
                </>}
            </div>
        </div>;
    }

    public renderContacts(contactInfo: IContactNode[]) {
        // const {isEditing} = this.props;
        const isEditing = this.props.isEditing || this.props.isCreatingNewEntity;

        return <>
            {
                <div className={`${rowClass} description_contactInfo`} key={'description_contactInfo'}>
                    <div className={'flex-row'}>
                        <div className='label'><b>Contacts: </b></div>
                        <div className='popupContactInfo'>
                            {contactInfo && contactInfo.length > 0 &&
                            contactInfo.map((contact, i) => this.renderContact(contact, i))}
                            {isEditing && <>
                                <div className={'description_contactInfo_row'}>
                                    <div>(existing contact) </div>
                                    <Button size={'small'} style={{marginLeft: 'auto'}}
                                            onClick={this._createLinkToContact}>Add Existing</Button>
                                </div>
                                <div className={'description_contactInfo_row'}>
                                    <div>(new contact) </div>
                                    <Button size={'small'} style={{marginLeft: 'auto'}}
                                            onClick={this._createNewContact}>Add New</Button>
                                </div>
                            </>}
                        </div>
                    </div>
                </div>
            }
        </>;
    }

    public renderPopupContent(popupInfo: IPopupInfo) {
        return (
            <div>
                {popupInfo.name &&
                popupInfo.name !== 'null' &&
                <div className={rowClass}>
                            <span>
                                <strong>Name: </strong><span className={selectClass}>{popupInfo.name}</span>
                            </span>
                </div>}
                <div className={rowClass}>
                            <span>
                                <strong>Address: </strong><span className={selectClass}>{popupInfo.address}</span>
                            </span>
                </div>
                <div className={rowClass}>
                            <span>
                                <strong>City: </strong><span className={selectClass}>{popupInfo.city}</span>
                            </span>
                </div>
                <div className={rowClass}>
                            <span>
                                <strong>State: </strong><span className={selectClass}>{popupInfo.state}</span>
                            </span>
                </div>
                <div className={rowClass}>
                            <span>
                                <strong>Zip: </strong><span className={selectClass}>{popupInfo.zip}</span>
                            </span>
                </div>

                <div className={rowClass}>
                            <span>
                                <strong>Jurisdiction: </strong><span className={selectClass}>{popupInfo.notes}</span>
                            </span>
                </div>

                {this.renderContacts(popupInfo.contacts)}

                {(popupInfo.contacts && popupInfo.contacts.length !== 0) && (<div className={rowClass}>
                        <span>
                            <strong>Emails: </strong>
                            <span className={selectClass}>{popupInfo.contacts.map(c => c.email).join(',')}</span>
                        </span>
                </div>)}

                {popupInfo.description.length > 0 && popupInfo.description.map((e, i) =>
                    <div className={rowClass} key={`description_${i}`}>
                        <>
                            <strong>{popupInfo.descriptionLabels[i]}</strong> : {
                            isUrl(e) ? <a href={e}>{e}</a> : <span className={selectClass}>{e}</span>
                        }
                        </>
                    </div>,
                )}

                <div className={`${rowClass}`} style={{display: 'flex', height: '40px'}}>
                    <div className='buttonRow'>
                        {/*<div className="styledButton" style={{backgroundColor: '#3b71fb'}}*/}
                        {/*     onClick={this.buffer}>Buffer</div>*/}
                        <div className='styledButton' style={{backgroundColor: '#c84f3e'}}
                             onClick={this.toggleEditing}>Edit</div>
                    </div>
                </div>
            </div>
        );
    }

    public renderEditablePopupContent(popupInfo: IPopupInfo) {
        const {isCreatingNewEntity} = this.props;
        const {entityValid} = this.state;
        return (
            <>
                {this.renderLayerSelect()}
                {this.priorityKeys.map((key, i) => {
                    return <div className={rowClass} key={`priorityKeys_${i}`}>
                            <span>
                                {/*<strong className="label">{this.priorityLabels[i]}: </strong>*/}
                                <Input addonBefore={this.priorityLabels[i]}
                                       size='small'
                                       maxLength={maxLengthInput}
                                       type='text' value={popupInfo[key] || ''}
                                       onChange={(e) => this.updateKey(e, key)}/>
                            </span>

                        {(!entityValid && !this._validateEntityKey(key)) &&
                        <div className={'modal-input-error'}>
                            {html_space()} Invalid value
                        </div>
                        }
                    </div>;

                })}

                {!isCreatingNewEntity && this.renderContacts(popupInfo.contacts)}

                {popupInfo.description.length > 0 && popupInfo.description
                    .filter((e, i) => popupInfo.readOnlyKeys.indexOf(popupInfo.descriptionLabels[i]) === -1)
                    .map((e, i) =>
                        <div className={rowClass} key={`description_${i}`}>
                        <span>

                        <strong className='label'>{popupInfo.descriptionLabels[i]} </strong>
                            {
                                // !popupInfo.description[i] || popupInfo.description[i].length < 50 ?
                                // <input type="text" value={popupInfo.description[i] || ''}
                                //        onChange={(e) => this.updateDescription(e, i)}/> :
                                <TextArea autoSize
                                          maxLength={maxLengthTextArea}
                                          value={popupInfo.description[i] || ''}
                                          onChange={(evt) =>
                                              this.updateDescription(evt, i)}/>
                            }

                        </span>
                            {(!entityValid && !this._validateEntityDescriptionKey(i)) &&
                            <div className={'modal-input-error'}>
                                {html_space()} Invalid value
                            </div>
                            }
                        </div>,
                    )}

                {!isCreatingNewEntity &&
                <div className={rowClass}>
                        <span>
                            <strong className='label'>Geometry </strong>
                            <Button size={'small'} onClick={this.editGeometry}>Edit</Button>
                        </span>
                </div>}

                {popupInfo.datelastchanged &&
                <div className={rowClass}>
                    <DateLastChanged date={popupInfo.datelastchanged}/>
                </div>}

                <div className={`${rowClass}`} style={{display: 'flex', height: '40px'}}>
                    <div className='buttonRow'>
                        {!isCreatingNewEntity && <div className='styledButton' style={{backgroundColor: '#d2362c'}}
                                                      onClick={this.delete}>Delete</div>}
                        <div className='styledButton' style={{backgroundColor: '#a2a2a2'}}
                             onClick={this.cancelEditing}>Cancel</div>
                        <div className={entityValid ? 'styledButton' : 'button-disabled styledButton'}
                             style={{backgroundColor: entityValid ? '#3b71fb' : '#afafaf'}}
                             onClick={this.saveEditing}>Save</div>
                    </div>
                </div>
            </>
        );
    }

    public renderLayerSelect() {
        const {layers = [], layerState} = this.props;
        const popupInfo: any = this.state.popupInfo || this.props.popupInfo;
        const geoType = getFeatureType(popupInfo.feature).toLowerCase();
        return (<div className={rowClass}>
                <span>
                    <b className='label'>Layer: </b>
                    <select value={popupInfo.type} onChange={(e) => this.updateKey(e, 'type')}>
                        <option value={''} style={{display: 'none'}}>Select Layer</option>
                        <option value={''} disabled>Select Layer</option>
                        {layers
                            .filter(layerId => {
                                return layerState[layerId].geoType.toLowerCase() === geoType;
                            })
                            .map(layerId =>
                                <option key={`popupContents-layerSelect-${layerId}`}
                                        value={layerId}>{getLayerName(layerId, layerState)}</option>)}
                    </select>
                </span>
        </div>);
    }

    renderPublicPopupContent (popupInfo) {
        return (
            <>
                {
                    popupInfo.name &&
                    popupInfo.name !== 'null' &&
                    <div className={rowClass}>
                            <span>
                                <b>Name: </b><span className={selectClass}>{popupInfo.name}</span>
                            </span>
                    </div>

                }
                {
                    <div className={rowClass}>
                            <span>
                                <b>Phone: </b><span className={selectClass}>{popupInfo.phone}</span>
                            </span>
                    </div>

                }
                {
                    <div className={rowClass}>
                            <span>
                                <b>Email: </b><span className={selectClass}>{popupInfo.email}</span>
                            </span>
                    </div>

                }
                {
                    renderUrl(popupInfo.websiteurl, 'Website', '', rowClass)
                }
            </>
        )
    }


    public render() {
        const popupInfo = this.state.popupInfo || this.props.popupInfo;
        const isEditing = this.props.isEditing || this.props.isCreatingNewEntity;
        const title = popupInfo.title || getLayerName(popupInfo.type!, this.props.layerState);

        return <div className={className}>
            {title && <div className={`${rowClass} popupHeader`}>
                        <span>
                            <b>Layer: </b><span className={`${selectClass}`}>{title}</span>
                        </span>
            </div>}
            <BasicModal
                invalid={!this.state.contactValid}
                onClose={this._resolveModal}
                cancel={this._hideContactDetails}
                show={this.props.showContactDetails}
                content={
                    <div className='contactPopupContent'>
                        {this._renderContactPopup()}
                    </div>
                }
                showCancel={this.state.contactInfoType !== contactInfoType.DETAILS}
                title={this._getContactPopupTitle()}
            />
            {this.renderPublicPopupContent(popupInfo)}
        </div>;
    }
}
